import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { SeriesDirective } from '@syncfusion/ej2-react-charts';
import axios from 'axios';
import PropTypes from 'prop-types';
import { ZingColors } from '../utils/colors';
import { colorWithOpacity } from '../utils/colorWithOpacity';
import { BACKEND_URL, chartColors, chartTypes } from '../utils/constants';
import poll from '../utils/poll';
import AreaChart from './AreaChart';
import BarChart from './BarChart';
import ComboChart from './ComboChart';
import DataTable from './DataTable';
import DonutChart from './DonutChart';
import LineChart from './LineChart';
import NoData from './NoData';
import ScatterPlotChart from './ScatterPlotChart';
import SingleValueChart from './SingleValueChart';
import StackedBarChart from './StackedBarChart';
import StackedLineChart from './StackedLineChart';

const ZingChart = ({
  apiKey,
  token,
  questionData,
  chartType,
  jobId,
  loadingQuestion,
  loadingValidation,
  forDashboard = false,
}) => {
  const [loadingChartResult, setLoadingChartResult] = useState(false);
  const [chartResult, setChartResult] = useState();
  const [error, setError] = useState();

  const fetchChartResult = useCallback(async () => {
    setLoadingChartResult(true);
    try {
      const result = await axios.get(
        `${BACKEND_URL}/api/v1/querymanager/${jobId}/embed?token=${token}&forDashboard=${forDashboard}`,
        {
          headers: {
            'x-zingdata-api': apiKey,
          },
        },
      );

      if (!result) {
        setError('Could not fetch result');
      }

      return result;
    } catch (e) {
      setError(e);
      setLoadingChartResult(false);
    }
  }, [apiKey, jobId, token, forDashboard]);

  useEffect(() => {
    if (jobId) {
      poll({
        fn: fetchChartResult,
        setFn: setChartResult,
        errorFn: setError,
        loadingFn: setLoadingChartResult,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const generateAxis = ({ chartType }) => {
    const { queryConstruct } = chartResult?.query || {};
    const qcChart = queryConstruct.chart;

    const isTimeSeriesQuestion = chartResult?.metadata?.isChronological;
    const isInverted = qcChart?.format?.inverted;

    let interval = 1;
    if (forDashboard && chartType !== chartTypes.SCATTER_PLOT) {
      interval = 5;
    } else if (chartType === chartTypes.HORIZONTAL_BAR_CHART) {
      interval = 3;
    }

    const primaryXAxis = {
      valueType: 'Category',
      isInversed: isTimeSeriesQuestion || isInverted,
      ...(qcChart.format.xAxis.title ? { title: qcChart.format.xAxis.title } : {}),
      ...(chartType !== chartTypes.HORIZONTAL_BAR_CHART && !forDashboard ? { labelRotation: '-45' } : {}),
      labelIntersectAction: 'Trim',
      desiredIntervals: 1,
      interval: interval,
      edgeLabelPlacement: 'Shift',
      majorGridLines: {
        width: qcChart.format.xAxis.gridLines == true ? 1 : 0,
        color: colorWithOpacity(ZingColors.neutralA100, 0.1),
      },
      majorTickLines: { size: qcChart.format.xAxis.ticks == true ? 4 : 0, color: ZingColors.surface5 },
      maximumLabelWidth: qcChart?.format.xAxis.labels == true ? 130 : 0,
      tickPosition: 'Inside',
      rangePadding: 'Additional',
    };

    const primaryYAxis = {
      labelFormat:
        qcChart?.format.yAxis.unit && qcChart?.format.yAxis.unit != 'none'
          ? `{value}${qcChart.format.yAxis.unit.value}`
          : '',
      numberFormat: qcChart?.format.yAxis.numberFormatValue,
      tickPosition: 'Inside',
      maximumLabelWidth: qcChart?.format.yAxis.labels == true ? 80 : 0,
      ...(qcChart.format.yAxis.title ? { title: qcChart.format.yAxis.title } : {}),
      majorGridLines: {
        width: qcChart.format.yAxis.gridLines == true ? 1 : 0,
        color: colorWithOpacity(ZingColors.neutralA100, 0.1),
      },
      majorTickLines: { size: qcChart.format.yAxis.ticks == true ? 0.5 : 0, color: ZingColors.surface5 },
      minorTickLines: { size: qcChart.format.yAxis.ticks == true ? 0.5 : 0, color: ZingColors.surface5 },
    };

    let axes = [];
    const hasY2Axis = !!qcChart?.axis?.y1?.length;
    if (hasY2Axis) {
      axes.push({
        name: 'yAxis2',
        opposedPosition: true,
        labelFormat:
          qcChart.format.yAxis2.unit && qcChart.format.yAxis2.unit != 'none'
            ? `{value}${qcChart.format.yAxis2.unit?.value}`
            : '',
        numberFormat: qcChart?.format.yAxis2.numberFormatValue,
        tickPosition: 'Inside',
        maximumLabelWidth: qcChart?.format.yAxis2.labels == true ? 80 : 0,
        ...(qcChart.format.yAxis2.title ? { title: qcChart.format.yAxis2?.title } : {}),
        majorGridLines: {
          width: qcChart.format.yAxis2.gridLines == true ? 1 : 0,
          color: colorWithOpacity(ZingColors.neutralA100, 0.1),
        },
        majorTickLines: { size: qcChart.format.yAxis2.ticks == true ? 0.5 : 0, color: ZingColors.surface5 },
        minorTickLines: { size: qcChart.format.yAxis2.ticks == true ? 0.5 : 0, color: ZingColors.surface5 },
      });
    }

    // const hasDimensions = qcChart?.axis?.x?.length > 1;
    // if (hasDimensions) {
    //   axes.push({
    //     name: 'xAxis2',
    //     valueType: 'Category',
    //     opposedPosition: true,
    //     visible: false,
    //   });
    // }

    return {
      primaryXAxis,
      primaryYAxis,
      ...(hasY2Axis ? { axes: axes } : {}),
    };
  };

  const chartPalette = useMemo(() => [...chartColors].splice(0, chartResult?.queryResult?.length), [chartResult]);

  const renderSeries = useCallback(({ series, type, index, width, qcChart }) => {
    if (!series) return null;

    let marker = {};
    if (qcChart?.format?.showLabels) {
      marker = {
        dataLabel: {
          visible: true,
          position: 'Outer',
          format: 'n2', // 2 decimal points
          font: {
            fontFamily: 'Noto Sans',
            size: '10px',
          },
        },
      };
    }

    const axisNames = Object.keys(series.metadata);
    const xName = axisNames?.length ? axisNames[0] : 'x';
    const yName = axisNames?.length ? axisNames[1] : 'y';

    return (
      <SeriesDirective
        key={index}
        type={type}
        dataSource={series.data || series}
        tooltipMappingName="r"
        xName={xName}
        yName={yName}
        name={
          (qcChart.format.seriesFormats?.length && qcChart.format.seriesFormats[index]?.updatedName) ||
          series.seriesname
        }
        yAxisName={yName === 'y1' ? 'yAxis2' : null}
        columnSpacing={0.1}
        marker={marker}
        width={width}
        // xAxisName={series.metadata?.x?.length > 1 ? 'xAxis2' : 'xAxis1'}
      />
    );
  }, []);

  let chart;

  switch (chartType) {
    case chartTypes.BAR_CHART:
    case chartTypes.HORIZONTAL_BAR_CHART: {
      let bar;
      if (!chartResult?.queryResult?.length) {
        bar = <NoData />;
      } else {
        bar = (
          <BarChart
            chartResult={chartResult}
            chartPalette={chartPalette}
            renderSeries={renderSeries}
            generateAxis={generateAxis}
            jobId={chartResult?.id}
            chartType={chartType}
          />
        );
      }

      chart = bar;
      break;
    }
    case chartTypes.STACKED_BAR_CHART:
    case chartTypes.STACKED_BAR_100_CHART: {
      let bar;
      if (!chartResult?.queryResult?.length) {
        bar = <NoData />;
      } else {
        bar = (
          <StackedBarChart
            chartResult={chartResult}
            chartPalette={chartPalette}
            renderSeries={renderSeries}
            generateAxis={generateAxis}
            jobId={chartResult?.id}
            chartType={chartType}
          />
        );
      }

      chart = bar;
      break;
    }
    case chartTypes.LINE_CHART: {
      let line;
      if (!chartResult?.queryResult?.length) {
        line = <NoData />;
      } else {
        line = (
          <LineChart
            chartResult={chartResult}
            chartPalette={chartPalette}
            renderSeries={renderSeries}
            generateAxis={generateAxis}
            jobId={chartResult?.id}
            chartType={chartType}
          />
        );
      }

      chart = line;
      break;
    }
    case chartTypes.STACKED_LINE_CHART: {
      let line;
      if (!chartResult?.queryResult?.length) {
        line = <NoData />;
      } else {
        line = (
          <StackedLineChart
            chartResult={chartResult}
            chartPalette={chartPalette}
            renderSeries={renderSeries}
            generateAxis={generateAxis}
            jobId={chartResult?.id}
            chartType={chartType}
          />
        );
      }

      chart = line;
      break;
    }
    case chartTypes.AREA_CHART: {
      let area;
      if (!chartResult?.queryResult?.length) {
        area = <NoData />;
      } else {
        area = (
          <AreaChart
            chartResult={chartResult}
            chartPalette={chartPalette}
            renderSeries={renderSeries}
            generateAxis={generateAxis}
            jobId={chartResult?.id}
            chartType={chartType}
          />
        );
      }

      chart = area;
      break;
    }
    case chartTypes.DONUT_CHART: {
      let donut;
      if (!chartResult?.queryResult?.length) {
        donut = <NoData />;
      } else {
        donut = (
          <DonutChart
            chartResult={chartResult}
            chartPalette={chartPalette}
            generateAxis={generateAxis}
            jobId={chartResult?.id}
            chartType={chartType}
          />
        );
      }

      chart = donut;
      break;
    }
    case chartTypes.DATA_TABLE: {
      let dataChart;
      if (
        (chartResult?.queryResult?.rows && !chartResult?.queryResult.rows.length) ||
        (!chartResult?.queryResult?.rows && !chartResult?.queryResult?.length)
      ) {
        dataChart = <NoData />;
      } else if (chartResult?.queryResult?.length === 1 && chartResult.queryResult[0].data?.length === 1) {
        dataChart = <SingleValueChart value={chartResult.queryResult[0].data[0]?.y} />;
      } else if (chartResult?.queryResult?.length > 1) {
        dataChart = <DataTable data={chartResult.queryResult} />;
      } else if (chartResult?.queryResult?.rows?.length) {
        dataChart = <DataTable data={chartResult?.queryResult?.rows} />;
      }

      chart = dataChart;
      break;
    }
    case chartTypes.BIG_NUMBER: {
      let bigNumberChart;
      if (!chartResult?.queryResult?.length) {
        bigNumberChart = <NoData />;
      } else {
        bigNumberChart = <SingleValueChart value={chartResult.queryResult[0].data[0]?.y} />;
      }

      chart = bigNumberChart;
      break;
    }
    case chartTypes.SCATTER_PLOT: {
      let line;
      if (!chartResult?.queryResult?.length) {
        line = <NoData />;
      } else {
        line = (
          <ScatterPlotChart
            chartResult={chartResult}
            chartPalette={chartPalette}
            renderSeries={renderSeries}
            generateAxis={generateAxis}
            jobId={chartResult?.id}
            chartType={chartType}
          />
        );
      }

      chart = line;
      break;
    }
    case chartTypes.COMBO_CHART: {
      let line;
      if (!chartResult?.queryResult?.length) {
        line = <NoData />;
      } else {
        line = (
          <ComboChart
            chartResult={chartResult}
            chartPalette={chartPalette}
            renderSeries={renderSeries}
            generateAxis={generateAxis}
            jobId={chartResult?.id}
            chartType={chartType}
          />
        );
      }

      chart = line;
      break;
    }
    default:
      break;
  }

  if (loadingQuestion || loadingChartResult || loadingValidation) {
    return (
      <div className="mb-3 mt-2" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!chartResult || error) {
    return <p>An error occurred: {error?.response?.data?.message}</p>;
  }

  if (forDashboard) {
    return chart;
  }

  return (
    <>
      <h4 className="mb-4">{questionData?.questionText}</h4>
      <div style={{ height: 'calc(100% - 40px)' }}>{chart}</div>
    </>
  );
};

ZingChart.propTypes = {
  apiKey: PropTypes.string.isRequired,
  token: PropTypes.string,
  where: PropTypes.string,
  className: PropTypes.string,
  questionData: PropTypes.object,
  chartType: PropTypes.string,
  jobId: PropTypes.string,
  loadingQuestion: PropTypes.bool,
  loadingValidation: PropTypes.bool,
  forDashboard: PropTypes.bool,
};

export default memo(ZingChart);
